import { Link } from "react-router-dom";
import { ArrowUpRight, Quote } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { MEDIA, SITE } from "@/lib/site";
import usePageMeta from "@/lib/usePageMeta";

const PHILOSOPHY = [
  {
    title: "Practical, not theoretical",
    body: "Frameworks are useful only when they serve outcomes. I bring proven practices — and the judgment to know when to set them aside.",
  },
  {
    title: "Business-focused",
    body: "Transformation that doesn't move the business forward isn't transformation. Every recommendation is grounded in your goals, constraints, and reality.",
  },
  {
    title: "Outcome-driven",
    body: "Progress is measurable: faster delivery, better alignment, calmer leadership rhythms. Activity is not the point — change is.",
  },
  {
    title: "Calm and discreet",
    body: "Senior leadership work requires discretion. Engagements are confidential, considered, and built on trust.",
  },
];

export default function AboutPage() {
  usePageMeta({
    title: "About",
    description:
      "Anna Zavarzina, Organizational Transformations Consultant — practical, business-focused agile transformation work for senior leadership teams.",
    path: "/about",
  });
  return (
    <div data-testid="page-about">
      {/* Intro */}
      <section className="relative" data-testid="about-intro">
        <div className="container-tight section-pad">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <Reveal>
                <div className="relative">
                  <div className="aspect-[4/5] w-full max-w-md overflow-hidden border border-brand-line bg-brand-surface3">
                    <img
                      src={MEDIA.portrait}
                      alt={`${SITE.name} — ${SITE.title}`}
                      className="h-full w-full object-cover"
                      data-testid="about-portrait"
                    />
                  </div>
                  <div className="absolute -bottom-4 -right-4 hidden sm:block border border-brand-accent bg-white px-5 py-3">
                    <p className="font-display text-sm tracking-tight text-brand-ink">
                      {SITE.name}
                    </p>
                    <p className="text-[11px] tracking-[0.18em] uppercase text-brand-muted mt-1">
                      {SITE.title}
                    </p>
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7 order-1 lg:order-2">
              <Reveal>
                <p className="overline">About</p>
                <h1
                  data-testid="about-heading"
                  className="mt-6 font-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] tracking-[-0.02em] text-brand-ink text-balance"
                >
                  A steady hand for organizations in motion.
                </h1>
              </Reveal>
              <Reveal delay={0.05}>
                <p className="mt-8 text-lg md:text-xl text-brand-muted leading-relaxed">
                  I'm Anna Zavarzina — an Organizational Transformations Consultant working with senior leaders who are accountable for real, measurable change. Through Progressive Innovations LLC, I help executive teams improve delivery, alignment, and leadership effectiveness without burning out the organization in the process.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-base md:text-lg text-brand-muted leading-relaxed">
                  My work blends assessment, coaching, and roadmap development with hands-on advisory through the difficult middle of the journey — the place where most transformations quietly lose momentum.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-10 flex flex-wrap gap-3">
                  {["Agile Transformation", "Executive Coaching", "Operating Model", "Delivery Flow", "Change Adoption"].map((t) => (
                    <span key={t} className="px-3.5 py-1.5 text-xs tracking-wide border border-brand-line text-brand-ink">
                      {t}
                    </span>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Pull quote */}
      <section className="bg-brand-surface2" data-testid="about-quote">
        <div className="container-tight section-pad">
          <Reveal>
            <Quote className="h-10 w-10 text-brand-accent" strokeWidth={1.25} />
            <p className="mt-8 font-display text-2xl sm:text-3xl lg:text-4xl leading-[1.25] tracking-tight text-brand-ink max-w-4xl text-balance">
              "The best transformations don't feel dramatic. They feel like the organization finally exhaling — clearer decisions, steadier delivery, fewer surprises."
            </p>
            <p className="mt-8 text-sm tracking-[0.18em] uppercase text-brand-muted">
              — Anna Zavarzina
            </p>
          </Reveal>
        </div>
      </section>

      {/* Philosophy */}
      <section data-testid="about-philosophy">
        <div className="container-tight section-pad">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="overline">Philosophy</p>
                <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] text-brand-ink text-balance">
                  How I work.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.05}>
                <p className="text-lg text-brand-muted leading-relaxed">
                  These principles aren't aspirational — they're the operating rules I bring to every engagement. They shape what I recommend, what I refuse, and how I work alongside leadership teams.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-brand-line border border-brand-line">
            {PHILOSOPHY.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div className="bg-white p-8 md:p-12 h-full">
                  <p className="number-tag">0{i + 1}</p>
                  <h3 className="mt-5 font-display text-2xl tracking-tight text-brand-ink">{p.title}</h3>
                  <p className="mt-4 text-[15px] text-brand-muted leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Background */}
      <section className="bg-brand-navy text-white" data-testid="about-background">
        <div className="container-tight section-pad">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="overline text-[#E8A487]">Background</p>
                <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] text-white text-balance">
                  Years of helping teams and leaders ship change that lasts.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7 space-y-6">
              <Reveal delay={0.05}>
                <p className="text-lg text-white/75 leading-relaxed">
                  I've worked with leadership teams across regulated industries, scaling product organizations, and enterprises navigating multi-year transformation programs. The contexts differ; the underlying work rarely does.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-base text-white/70 leading-relaxed">
                  My focus is on the leadership behaviors, operating rhythms, and delivery systems that quietly determine whether change actually sticks. I bring deep grounding in agile, lean, and organizational design — and the experience to know when each is the right tool, and when none of them are.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="pt-8">
                  <Link to="/contact" data-testid="about-cta-book" className="inline-flex items-center gap-2 bg-white text-brand-ink px-6 py-3.5 text-sm font-medium tracking-wide transition-all duration-200 hover:bg-[#E8A487] hover:-translate-y-[1px]">
                    Book a Discovery Call
                    <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
